<script setup lang="ts">
import { getIsCustomizedApp } from '@shared/appConfig';
import KgrIcon from '@shared/components/KgrIcon.vue';
import PrivacyMenuItem from '@shared/components/PrivacyMenuItem.vue';
import { useAuthImage } from '@shared/composables/useAuthImage';
import { useModuleEnabled } from '@shared/composables/useModuleEnabled';
import { useAppStateStore } from '@shared/stores/app-state';
import { useOrganizationStore } from '@shared/stores/organization';
import { useUserStore } from '@shared/stores/user';
import { computed } from 'vue';
import { RouterLink } from 'vue-router';

const orgStore = useOrganizationStore();
const appState = useAppStateStore();
const userStore = useUserStore();
const chatEnabled = useModuleEnabled('Chat');

const org = computed(() => orgStore.organization);
const isCustomizedApp = getIsCustomizedApp();
const staticLogo = !!window.kgrDynamicConfig.staticLogo;

// API logo only when the organization has one and `staticLogo` is not forced; otherwise the
// static client logo (1:1 Angular main-menu.html).
const logoApiUrl = computed(() => {
    if (staticLogo || !org.value?.hasLogo) {
        return null;
    }
    const { apiHost } = window.kgrDynamicConfig;
    return `${apiHost}${org.value.logoApiUrl}`;
});

const logoObjectUrl = useAuthImage(logoApiUrl);
// Angular reads `clientLogoInMenuFileName || clientLogoFileName` here — a client may ship a
// separate logo for the menu (e.g. hedi's text-free variant).
const menuLogoFileName = window.kgrDynamicConfig.clientLogoInMenuFileName || window.kgrDynamicConfig.clientLogoFileName || 'logo.svg';
const fallbackLogoSrc = `${import.meta.env.BASE_URL}assets/client/${menuLogoFileName}`;
</script>

<template>
    <div class="scrollWrapper kgr-scrollable">
        <div class="logoRegion kgr-region kgr-text kgr-text--centered">
            <RouterLink
                to="/"
                class="kgr-block kgr-region kgr-region--small kgr-region--lightGray kgr-region--forceLightScheme kgr-region--rounded"
            >
                <img
                    v-if="logoObjectUrl"
                    :src="logoObjectUrl"
                    alt="Logo"
                    class="logo"
                />
                <img
                    v-else
                    :src="fallbackLogoSrc"
                    alt="Logo"
                    class="logo"
                />
            </RouterLink>
        </div>

        <div class="kgr-region">
            <nav class="kgr-verticalMenu">
                <template v-if="userStore.isLoggedIn">
                    <template v-if="appState.isScreenMediumAndUp">
                        <RouterLink
                            to="/news"
                            class="kgr-verticalMenu__item is-level-1"
                            active-class="is-active"
                        >
                            <span class="kgr-verticalMenu__itemIcon"><KgrIcon
                                name="notifications"
                                :small="true"
                            /></span>
                            <span class="kgr-verticalMenu__itemLabel">{{ $t('News') }}</span>
                        </RouterLink>
                        <RouterLink
                            to="/events"
                            class="kgr-verticalMenu__item is-level-1"
                            active-class="is-active"
                        >
                            <span class="kgr-verticalMenu__itemIcon"><KgrIcon
                                name="calendar"
                                :small="true"
                            /></span>
                            <span class="kgr-verticalMenu__itemLabel">{{ $t('Termine') }}</span>
                        </RouterLink>
                        <RouterLink
                            v-if="chatEnabled"
                            to="/chats"
                            class="kgr-verticalMenu__item is-level-1"
                            active-class="is-active"
                        >
                            <span class="kgr-verticalMenu__itemIcon"><KgrIcon
                                name="chat"
                                :small="true"
                            /></span>
                            <span class="kgr-verticalMenu__itemLabel">{{ $t('Chats') }}</span>
                        </RouterLink>
                        <RouterLink
                            to="/groups"
                            class="kgr-verticalMenu__item is-level-1"
                            active-class="is-active"
                        >
                            <span class="kgr-verticalMenu__itemIcon"><KgrIcon
                                name="users"
                                :small="true"
                            /></span>
                            <span class="kgr-verticalMenu__itemLabel">{{ $t('Gruppen') }}</span>
                        </RouterLink>
                    </template>
                    <RouterLink
                        to="/diet"
                        class="kgr-verticalMenu__item is-level-1"
                        active-class="is-active"
                    >
                        <span class="kgr-verticalMenu__itemIcon"><KgrIcon
                            name="fastfood"
                            :small="true"
                        /></span>
                        <span class="kgr-verticalMenu__itemLabel">{{ $t('Speiseplan') }}</span>
                    </RouterLink>
                    <RouterLink
                        to="/files"
                        class="kgr-verticalMenu__item is-level-1"
                        active-class="is-active"
                    >
                        <span class="kgr-verticalMenu__itemIcon"><KgrIcon
                            name="folder-shared"
                            :small="true"
                        /></span>
                        <span class="kgr-verticalMenu__itemLabel">{{ $t('Dokumente') }}</span>
                    </RouterLink>
                    <RouterLink
                        to="/galleries"
                        class="kgr-verticalMenu__item is-level-1"
                        active-class="is-active"
                    >
                        <span class="kgr-verticalMenu__itemIcon"><KgrIcon
                            name="photo-gallery"
                            :small="true"
                        /></span>
                        <span class="kgr-verticalMenu__itemLabel">{{ $t('Fotos') }}</span>
                    </RouterLink>
                </template>

                <RouterLink
                    v-if="userStore.isLoggedIn || isCustomizedApp"
                    to="/organization/contact"
                    class="kgr-verticalMenu__item is-level-1"
                    active-class="is-active"
                >
                    <span class="kgr-verticalMenu__itemIcon"><KgrIcon
                        name="comment"
                        :small="true"
                    /></span>
                    <span class="kgr-verticalMenu__itemLabel">{{ $t('Kontakt') }}</span>
                </RouterLink>

                <PrivacyMenuItem />
            </nav>
        </div>
    </div>
</template>

<style scoped lang="scss">
.logo {
    display: inline-block;
    max-height: 80px;
    max-width: 90%;
}

html:not([data-kgr-theme='dark']) .logoRegion {
    padding-top: 0;
    padding-bottom: 0;
}

.scrollWrapper {
    // Angular `:host { display: block; height: 100dvh }` — full height so the bottom padding
    // (mobile browser nav bar) takes effect reliably.
    display: block;
    height: 100dvh;
    padding-bottom: 120px;
}
</style>
